'use client';

import flush from 'just-flush';
import { WorkgroupHero } from '@stereoagency/ui';
import compact from 'just-compact';
import { defaultLanguage } from '@stereoagency/constants';
import { researchGroupPageQuery } from './researchGroupPageQuery';
import {
  findInKeyArray,
  formatPhoneNumberLink,
  formatProfileName,
  platformFromURL,
  sanityImageAsProps
} from '@/utils';
import { SingleProfile } from '../GroupOrPlatformDepartmentsSection.client';

type Props = {
  lang: string;
  researchGroup: (typeof researchGroupPageQuery)[1];
  researchGroupLeaderProfile?: SingleProfile;
};

export default function ResearchGroupPageHero({
  lang,
  researchGroup,
  researchGroupLeaderProfile
}: Props) {
  const { badges } = researchGroup;

  const subtitle = compact(
    researchGroup.taxonomies?.map((tax) =>
      tax.name
        ? findInKeyArray(tax.name, [lang, defaultLanguage.id])?.value
        : null
    ) ?? []
  ).join(` & `);

  const links = flush([
    researchGroupLeaderProfile?.email
      ? [
          `mailto:${researchGroupLeaderProfile.email}`,
          researchGroupLeaderProfile.email
        ]
      : null,
    researchGroupLeaderProfile?.phone
      ? [
          formatPhoneNumberLink(researchGroupLeaderProfile.phone),
          researchGroupLeaderProfile.phone
        ]
      : null,
    researchGroupLeaderProfile?.website
      ? [
          researchGroupLeaderProfile.website,
          researchGroupLeaderProfile.website.replace(/^[a-z]:\/\//, '')
        ]
      : null
  ]);

  if (
    !researchGroupLeaderProfile?.image?.asset._ref ||
    !researchGroupLeaderProfile.name
  ) {
    return null;
  }

  return (
    <WorkgroupHero
      image={sanityImageAsProps(researchGroupLeaderProfile.image)}
      title={formatProfileName(researchGroupLeaderProfile)}
      subtitle={subtitle}
      description={
        !!researchGroupLeaderProfile?.address && (
          <p>
            <strong className="whitespace-pre-line">
              {researchGroupLeaderProfile?.address}
            </strong>
          </p>
        )
      }
      links={links.map(([link, label]) => ({
        target: '_blank',
        href: link,
        label
      }))}
      socialItems={researchGroupLeaderProfile.socials?.map((social) => ({
        href: social,
        platform: platformFromURL(social),
        target: '_blank',
      }))}
      badges={flush(
        badges?.map((badge) =>
          badge.image?.asset._ref && badge.action?.url
            ? {
                image: sanityImageAsProps(badge.image),
                action: { href: badge.action?.url }
              }
            : null
        ) ?? []
      )}
      accentColor="--colors-yellow-700"
    />
  );
}
